export interface FaqItem {
  q: string;
  a: string;
}

/** Builds a schema.org FAQPage JSON-LD block from a page's FAQ list. */
export const faqSchema = (faqs: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const homeFaqs: FaqItem[] = [
  {
    q: 'How much tax will I pay on my salary in 2025/26?',
    a: 'The first £12,570 of your annual salary is tax-free (the Personal Allowance). Above that, you pay 20% Income Tax up to £50,270, then 40% up to £125,140, then 45% above that. On top of Income Tax, most employees pay 8% National Insurance on earnings between £12,570 and £50,270, and 2% above that. Use the calculator above to see the exact breakdown for your specific salary.',
  },
  {
    q: 'What are the UK tax brackets for 2025/26?',
    a: 'UK Income Tax bands for 2025/26: Personal Allowance £0–£12,570 (0%), Basic rate £12,571–£50,270 (20%), Higher rate £50,271–£125,140 (40%), Additional rate above £125,140 (45%). Scottish taxpayers use six separate bands. The Personal Allowance tapers away above £100,000, creating a 60% effective marginal rate between £100,000 and £125,140.',
  },
  {
    q: 'What is PAYE and how does it work?',
    a: 'PAYE (Pay As You Earn) is the system HMRC uses to collect Income Tax and National Insurance directly from your wages before they reach your bank account. Your employer deducts the correct amount each pay period based on your tax code — so most employees never need to file a tax return. Your payslip shows exactly how much was deducted under PAYE.',
  },
  {
    q: 'What is take-home pay and how is it different from gross salary?',
    a: 'Your gross salary is the full amount your employer pays — the headline number in your contract. Take-home pay (net pay) is what actually lands in your bank account after Income Tax, National Insurance, pension contributions, and any student loan repayments are deducted. For a typical employee on £35,000, take-home pay is roughly £27,800 a year, or about £2,317 a month.',
  },
  {
    q: 'How do I use a reverse tax calculator to find my gross salary from take-home pay?',
    a: 'A reverse tax calculator works backwards: you enter the monthly or annual net pay you want to take home, and it tells you the gross salary you would need to earn to achieve that figure after Income Tax, NI, pension, and student loan deductions. Use our dedicated reverse take-home pay calculator for this.',
  },
];

export const grossToNetFaqs: FaqItem[] = [
  {
    q: 'What is the difference between gross and net pay?',
    a: 'Gross pay is your salary before any deductions — the headline figure in your contract. Net pay (take-home pay) is what actually lands in your bank account after Income Tax, National Insurance, pension contributions and student loan repayments are taken off.',
  },
  {
    q: 'How is UK take-home pay calculated for 2025/26?',
    a: 'Take-home pay = gross salary minus Income Tax, National Insurance, workplace pension contributions and any student loan repayments. The standard Personal Allowance is £12,570 (tax-free); income above that is taxed at 20% up to £50,270, 40% up to £125,140, and 45% above that, with National Insurance charged at 8% and then 2% on the same bands.',
  },
  {
    q: 'How do I convert gross to net salary in the UK?',
    a: 'To convert a gross salary to net pay: (1) subtract the Personal Allowance (£12,570) to get taxable income; (2) apply Income Tax at 20%/40%/45% on the relevant bands; (3) apply National Insurance at 8% between £12,570–£50,270 and 2% above; (4) subtract any pension contribution and student loan repayment. This calculator does all four steps automatically for any gross salary you enter.',
  },
  {
    q: 'Can I work backwards from net to gross salary?',
    a: 'Yes — use the net to gross salary calculator linked on this page. Enter the monthly or annual take-home pay you want to achieve, and it calculates the gross salary required to produce that figure after all PAYE deductions. This is useful when negotiating a salary to hit a specific take-home target.',
  },
  {
    q: 'Does this calculator work for Scotland, NHS, or self-employed income?',
    a: "This calculator covers standard England/Wales/NI PAYE employment. For Scottish tax bands, NHS Agenda for Change pay, IR35 contracting, or self-employment, use the dedicated calculators — each applies the correct rules for that situation rather than approximating them here.",
  },
  {
    q: 'Why does my payslip show a different figure to this calculator?',
    a: "Small differences usually come from your employer's payroll software rounding pay-period figures slightly differently, a cumulative tax code adjustment carried over from a previous period, or a benefit-in-kind or one-off deduction this calculator doesn't know about. For a like-for-like check, make sure your tax code, pension percentage and student loan plan here match your actual payslip exactly.",
  },
];

export const nhsFaqs: FaqItem[] = [
  {
    q: 'How is NHS take-home pay calculated?',
    a: 'NHS pay is based on your Agenda for Change band and pay point, plus High Cost Area Supplement (HCAS) if you work in London or the fringe. From that gross figure, Income Tax, 8% employee National Insurance, and NHS Pension Scheme contributions (tiered between 5.2% and 12.5% depending on pensionable pay) are deducted.',
  },
  {
    q: 'What are the NHS pension contribution tiers?',
    a: 'NHS Pension Scheme contributions are tiered by actual pensionable pay: 5.2% for the lowest band, then 6.5%, 8.3%, 9.8%, 10.7%, up to 12.5% for pay over £67,669 (2026/27 thresholds). The rate percentages are unchanged from 2025/26 — only the pay boundaries moved. Contributions come off your gross pay before Income Tax under a net pay arrangement.',
  },
  {
    q: 'Does this calculator include the 2026/27 NHS pay award (26/27)?',
    a: 'Yes. Use the 2025/26 ⇄ 2026/27 toggle in the calculator. The 2026/27 Agenda for Change award is a 3.3% consolidated uplift effective 1 April 2026 for England, Wales and Northern Ireland, and was paid in April salaries rather than backdated. The 2025/26 scales remain selectable for checking older payslips.',
  },
  {
    q: 'Can I work out NHS part-time take-home pay?',
    a: 'Yes — enter your contracted hours per week (full-time is 37.5). The calculator pro-rates your basic Agenda for Change pay and the HCAS London weighting floor, and sets your NHS pension contribution tier from your actual part-time pensionable pay, which is often a lower tier than the full-time equivalent.',
  },
  {
    q: 'What is HCAS and does it apply to me?',
    a: 'High Cost Area Supplement is an additional percentage on top of your basic Agenda for Change salary for staff working in Inner London, Outer London, or the London fringe, to reflect the higher cost of living. It does not apply outside these zones.',
  },
  {
    q: 'What is Band 8a salary in the NHS for 2026/27?',
    a: 'NHS Band 8a salary ranges from £57,528 (entry point) to £64,750 (top point) for 2026/27 after the 3.3% Agenda for Change award, up from £55,690–£62,682 in 2025/26. Band 8a covers senior roles such as matrons, advanced clinical specialists, lead allied health professionals, and service managers. Monthly take-home at entry is roughly £3,350 after Income Tax, NI and a 10.7% pension contribution.',
  },
  {
    q: 'What is NHS Band 7 salary in 2026/27?',
    a: 'NHS Band 7 salary ranges from £49,387 (entry point) to £56,515 (top point) for 2026/27, up from £47,810–£54,710 in 2025/26. Band 7 covers advanced nurse practitioners, ward managers, team leaders, and senior specialist allied health professionals. Monthly take-home at Band 7 entry (England, no London weighting) is approximately £2,930 after Income Tax, 8% NI, and NHS pension contributions.',
  },
  {
    q: 'What is the difference between Band 8a and Band 8b in the NHS?',
    a: 'Band 8a (£57,528–£64,750 for 2026/27) and Band 8b (£66,582–£77,368) are both senior management and specialist grades. Band 8a typically covers matrons, lead consultants, and departmental managers. Band 8b covers more senior roles with broader organisational responsibility — such as head of a nursing department or senior service manager. On pension, Band 8a sits at the 10.7% contribution tier throughout, while Band 8b crosses into the 12.5% tier partway up the band.',
  },
  {
    q: 'What roles are Band 8a in the NHS?',
    a: 'Common Band 8a roles include: Matron, Advanced Clinical Practitioner (ACP), Lead Pharmacist, Superintendent Radiographer, Consultant Nurse, Advanced Physiotherapist, Clinical Lead (specialist services), Service Manager, and Business Manager. The band is used across clinical, scientific, and managerial specialties where a high level of professional or managerial expertise is required.',
  },
];

export const scottishFaqs: FaqItem[] = [
  {
    q: 'Why does Scotland have different Income Tax rates?',
    a: 'Income Tax on non-savings, non-dividend income is a devolved power for the Scottish Parliament. It sets its own bands and rates each year, while National Insurance and the Personal Allowance remain UK-wide and are set by Westminster.',
  },
  {
    q: 'How many Scottish tax bands are there in 2025/26?',
    a: 'Six: starter (19%), basic (20%), intermediate (21%), higher (42%), advanced (45%), and top (48%) — compared with three bands in England, Wales and Northern Ireland (20%, 40%, 45%).',
  },
  {
    q: 'Do Scottish taxpayers pay more or less than the rest of the UK?',
    a: 'It depends on income. Because of the starter and intermediate bands, many lower and middle earners pay marginally less or a similar amount. Higher earners generally pay more, since the higher and top rates apply at lower thresholds and higher percentages than the rest of the UK.',
  },
  {
    q: 'Does my National Insurance change if I live in Scotland?',
    a: 'No. National Insurance rates and thresholds are set by the UK government and apply the same way regardless of which UK nation you live in — only Income Tax bands differ for Scottish taxpayers.',
  },
];

export const ir35Faqs: FaqItem[] = [
  {
    q: 'What does inside IR35 mean for my take-home pay?',
    a: "If a contract is deemed inside IR35, you're taxed broadly like an employee on that income — Income Tax and National Insurance are deducted through PAYE, either by the fee-payer/agency or via an umbrella company, before you receive payment. You lose the tax efficiency of paying yourself through a limited company.",
  },
  {
    q: 'What does outside IR35 mean?',
    a: 'Outside IR35 means HMRC treats you as genuinely self-employed for that contract. You can be paid gross into your limited company, then decide how to extract income — typically a mix of a small salary and dividends — which is usually more tax-efficient than PAYE.',
  },
  {
    q: 'Who decides if a contract is inside or outside IR35?',
    a: 'For most medium and large private-sector clients, and all public-sector clients, the end client is legally responsible for determining status and must issue a Status Determination Statement (SDS). Only contractors working for small private companies still determine their own status.',
  },
  {
    q: 'How much does an umbrella company take?',
    a: "Umbrella companies charge a margin — typically £15 to £30 per week or a fixed monthly fee — on top of employer National Insurance and the Apprenticeship Levy, both of which come out of the contract rate before you're paid, not out of your own pocket separately.",
  },
];

export const contractorFaqs: FaqItem[] = [
  {
    q: 'How much does a contractor take home on £450 a day?',
    a: "At £450 a day over 220 working days (£99,000 a year), a contractor working outside IR35 through a limited company takes home roughly £65,000 a year after Corporation Tax and dividend tax. Working inside IR35 through an umbrella, the same rate nets around £60,900 after employer NI, the Apprenticeship Levy, the umbrella margin, Income Tax and employee NI. Enter your own rate above for exact figures.",
  },
  {
    q: 'Is it better to contract through a limited company or an umbrella?',
    a: "For an outside-IR35 contract, a limited company (personal service company) is usually more tax-efficient — you pay Corporation Tax on profit, then extract a small salary plus dividends. For an inside-IR35 contract, an umbrella is simpler and the tax outcome is similar to PAYE either way. The calculator shows the take-home difference for your rate.",
  },
  {
    q: 'What deductions come out of a contract day rate?',
    a: "Inside IR35 via an umbrella: the umbrella margin, employer National Insurance (13.8% above £9,100), the Apprenticeship Levy (0.5%), then your own Income Tax and National Insurance. Outside IR35 via a limited company: allowable business expenses, Corporation Tax (19%–25%), then personal tax on the salary and dividends you draw.",
  },
  {
    q: 'How many working days should I assume in a year?',
    a: "A common planning assumption is 220 billable days — 260 weekdays minus about 5.6 weeks for annual leave, bank holidays and a buffer for gaps between contracts. Adjust the working-days figure in the calculator to match how much of the year you expect to be on contract.",
  },
];

export const umbrellaFaqs: FaqItem[] = [
  {
    q: 'Why is my umbrella take-home lower than my day rate suggests?',
    a: "The assignment rate paid to the umbrella has to cover the employment costs before you are paid a salary: the umbrella's margin, employer National Insurance (13.8% above £9,100) and the Apprenticeship Levy (0.5%), plus holiday pay is usually rolled into the rate. What is left becomes your gross salary, which is then taxed through PAYE like any employee.",
  },
  {
    q: 'What is a fair umbrella company margin?',
    a: "Most umbrella margins are £15–£30 a week (or £65–£130 a month) and this is the only part that is genuinely the umbrella's fee. Employer NI and the Apprenticeship Levy are statutory costs, not the umbrella's profit — be wary of providers advertising very low margins but inflating other deductions.",
  },
  {
    q: 'Do I pay employer National Insurance through an umbrella?',
    a: "Legally the umbrella is your employer and pays employer NI, but in practice it is funded from the assignment rate the agency agrees with the umbrella — so economically it reduces your take-home. A compliant umbrella shows employer NI and the Apprenticeship Levy as separate lines on your payslip or reconciliation statement.",
  },
  {
    q: 'Should I use an umbrella or go inside-IR35 PAYE through the agency?',
    a: "Take-home is usually similar. An umbrella adds a margin but consolidates holiday pay, pension and continuity of employment across assignments; agency PAYE has no margin but you are re-employed for each contract. If you are outside IR35, a limited company is generally more tax-efficient than either — compare on the contractor calculator.",
  },
];

export const pensionFaqs: FaqItem[] = [
  {
    q: 'How does salary sacrifice save more tax than a normal pension?',
    a: 'With salary sacrifice, you give up part of your gross salary in exchange for an equivalent employer pension contribution. Because your gross pay is reduced before Income Tax and National Insurance are calculated, you save both — not just Income Tax relief as with a standard relief-at-source pension.',
  },
  {
    q: 'Does salary sacrifice reduce my take-home pay?',
    a: 'It reduces your gross salary on paper, but because you pay less Income Tax and National Insurance on the remainder, your actual take-home pay typically falls by less than the amount sacrificed — the rest effectively comes from tax and NI you would otherwise have paid.',
  },
  {
    q: 'Can salary sacrifice affect my mortgage application or other benefits?',
    a: "Yes — because salary sacrifice lowers your contractual gross salary, some lenders and means-tested benefit calculations use the reduced figure rather than your pre-sacrifice salary. It's worth checking with a mortgage adviser if you're planning a large sacrifice ahead of applying.",
  },
  {
    q: 'Is salary sacrifice worth it if I earn just over £100,000?',
    a: 'Often, yes — sacrificing enough to bring your taxable income back under £100,000 restores your full Personal Allowance, which is taxed away at a 60% effective rate between £100,000 and £125,140. This makes salary sacrifice particularly valuable in that income band.',
  },
];

export const reverseFaqs: FaqItem[] = [
  {
    q: 'Why would I need to work out gross salary from net pay?',
    a: "It's useful when negotiating a job offer around a take-home figure you need to hit, setting a freelance day rate, or working out what salary to ask for so a specific amount lands in your bank account each month after tax, NI, pension and student loan deductions.",
  },
  {
    q: 'How accurate is a net-to-gross calculation?',
    a: "It works backwards through the same tax rules as a normal gross-to-net calculation, so it's accurate for standard PAYE employment. It assumes a consistent tax code and no one-off variables like bonuses, which can shift the exact figure slightly.",
  },
  {
    q: 'Does this account for pension contributions and student loans?',
    a: "Yes — the required gross salary is calculated using the same pension and student loan settings as the main calculator, so the target net figure reflects deductions you'd actually see on a payslip, not just Income Tax and National Insurance.",
  },
];

export const comparisonFaqs: FaqItem[] = [
  {
    q: 'Why compare take-home pay instead of just the headline salary?',
    a: 'Two offers with the same headline salary can produce different take-home pay if pension contributions, benefits, or tax code assumptions differ. Comparing net pay side by side shows what actually changes in your bank account, not just what looks better on a contract.',
  },
  {
    q: 'Should I also compare pension contributions between offers?',
    a: 'Yes — a lower salary with a higher employer pension contribution can sometimes leave you better off overall, even if take-home pay is slightly lower today. This calculator lets you set different pension percentages for each option to see that trade-off.',
  },
  {
    q: 'Does a pay rise always mean more take-home pay?',
    a: 'Not proportionally — as you move into a higher tax band, or lose Personal Allowance above £100,000, a bigger raise can be taxed at a higher marginal rate, so the net increase is smaller than the gross increase. This comparison shows both figures for clarity.',
  },
];

export const taxCodeFaqs: FaqItem[] = [
  {
    q: 'How do I find my tax code?',
    a: "Your tax code is shown on your payslip, your P60, or your P45 if you've left a job. You can also check or update it via your Personal Tax Account on GOV.UK.",
  },
  {
    q: 'What does the number in my tax code mean?',
    a: 'The number, multiplied by 10, is roughly your tax-free Personal Allowance for the year. The standard code 1257L means £12,570 of tax-free income before Income Tax applies.',
  },
  {
    q: 'Why do I have an emergency tax code?',
    a: "Emergency codes like 1257L W1 or 1257L M1 are used when your employer doesn't have your full tax history — commonly when starting a new job without a P45. They calculate tax non-cumulatively, which can mean overpaying tax temporarily until HMRC issues a corrected code.",
  },
  {
    q: 'What should I do if my tax code looks wrong?',
    a: "Check it against your circumstances using this decoder, then contact HMRC directly through your Personal Tax Account or by phone if it doesn't match — for example, if you have a K code but don't recognise the untaxed income it implies.",
  },
];

export const selfEmployedFaqs: FaqItem[] = [
  {
    q: 'How much tax do I pay as a self-employed sole trader?',
    a: 'You pay Income Tax on your profit (turnover minus allowable expenses) at the same rates as employees — 20% basic rate, 40% higher rate, 45% additional rate — plus Class 4 National Insurance at 6% on profit between £12,570 and £50,270, and 2% above that. There is no employer-style National Insurance to pay on self-employment income.',
  },
  {
    q: "What's the difference between the Trading Allowance and claiming expenses?",
    a: "The £1,000 Trading Allowance is a flat deduction you can claim instead of your actual business expenses, with no need to keep receipts. If your real expenses are higher than £1,000, claiming them instead gives you a bigger deduction and lower tax bill — but you can't claim both on the same income.",
  },
  {
    q: 'Do self-employed people pay Class 2 National Insurance?',
    a: "Compulsory Class 2 National Insurance was abolished from April 2024. If your profit is above the £6,725 Small Profits Threshold, you're treated as having paid it for state pension purposes without actually paying anything. Below that threshold, you can still choose to pay it voluntarily at £3.50/week to protect your state pension record.",
  },
  {
    q: 'How much should I set aside for my Self Assessment tax bill?',
    a: 'A common rule of thumb is 25-30% of turnover for a typical sole trader, though the exact figure depends on your expenses and profit level. This calculator shows your precise set-aside amount — Income Tax, Class 4 NI and any student loan repayment combined — based on your actual numbers rather than a flat estimate.',
  },
  {
    q: 'Do I need to register for Self Assessment?',
    a: 'Yes, once your gross self-employment income exceeds £1,000 in a tax year. You must register with HMRC by 5 October following the end of that tax year, and file your return by the following 31 January.',
  },
  {
    q: 'Does student loan repayment work differently for self-employed people?',
    a: "No — the same plan thresholds and rates apply (9% above your plan's threshold, or 6% above £21,000 for Postgraduate Loans), but instead of being deducted automatically through PAYE, it's calculated and paid as part of your Self Assessment bill based on your total profit for the year.",
  },
];

export const bonusFaqs: FaqItem[] = [
  {
    q: 'Is a bonus taxed differently to normal salary?',
    a: "No — there's no separate \"bonus tax rate\". A bonus is added to your annual income and taxed at your normal marginal rate. It can feel like it's taxed more heavily if it pushes part of your income into a higher tax band for the first time.",
  },
  {
    q: 'Why does my bonus feel like it was taxed at 40% or more?',
    a: "If your salary is already close to a tax band threshold (like £50,270), a bonus on top can tip some or all of it into the 40% higher-rate band, plus National Insurance. Your regular salary isn't retaxed — only the bonus portion above the threshold is taxed at the higher rate.",
  },
  {
    q: 'Can I reduce the tax on a bonus?',
    a: 'Yes — many employers let you sacrifice some or all of a bonus into your pension instead of taking it as cash. Because salary sacrifice reduces your taxable income before tax and NI are calculated, this can save significantly more than taking the bonus as pay, especially if it would otherwise push you into a higher tax band.',
  },
];

export const hourlyToSalaryFaqs: FaqItem[] = [
  {
    q: 'How do I convert an hourly rate to an annual salary?',
    a: 'Multiply your hourly rate by your contracted hours per week, then by 52 weeks. For example, £15/hour at 37.5 hours a week is £15 × 37.5 × 52 = £29,250 a year, before tax and National Insurance.',
  },
  {
    q: 'Does this account for holiday pay?',
    a: "This calculator assumes a standard salaried role where holiday is already paid as part of your annual salary. If you're paid purely by the hour with holiday pay added separately (common for casual or agency work), your actual annual income may be a little higher than the figure shown here.",
  },
];

export const proRataFaqs: FaqItem[] = [
  {
    q: 'How is pro rata salary calculated?',
    a: 'Pro rata salary = full-time equivalent (FTE) salary × (your hours ÷ full-time hours). For a £35,000 FTE role on a standard 37.5-hour week, working 22.5 hours gives £35,000 × (22.5 ÷ 37.5) = £21,000 a year.',
  },
  {
    q: 'What counts as "full-time hours" for this calculation?',
    a: 'This calculator uses the common UK standard of 37.5 hours a week. Some employers use 35 or 40 hours as their full-time baseline instead — check your contract or the job advert for the FTE hours used, since it changes the pro rata figure.',
  },
];

export const overtimeFaqs: FaqItem[] = [
  {
    q: 'Is overtime pay taxed at a higher rate?',
    a: "No — overtime is taxed exactly like your regular salary, added to your total income for the year. It can feel like it's taxed more if it pushes part of your income into a higher tax band, but there's no special \"overtime tax rate\".",
  },
  {
    q: 'Does overtime affect my pension contribution?',
    a: 'If your pension contribution is calculated as a percentage of total pay, overtime earnings usually increase your pension contribution too, since they widen your pensionable pay. Check your specific scheme rules, as this varies by employer.',
  },
];

export const minimumWageFaqs: FaqItem[] = [
  {
    q: 'What is the National Living Wage for 2025/26?',
    a: 'The National Living Wage for workers aged 21 and over is £12.21 an hour from April 2025. The rate for 18-20 year olds is £10.00/hour, and £7.55/hour for under-18s and apprentices.',
  },
  {
    q: 'Do you pay tax on minimum wage?',
    a: 'Yes, if your total annual income is above the £12,570 Personal Allowance. A full-time worker on the National Living Wage earns well above this, so both Income Tax and National Insurance apply to the portion above £12,570.',
  },
  {
    q: 'Is minimum wage the same across the whole UK?',
    a: 'Yes — the National Living Wage and National Minimum Wage rates are set UK-wide and apply the same way in England, Scotland, Wales and Northern Ireland. Only the Income Tax bands differ for Scottish taxpayers, not the minimum wage itself.',
  },
];

export const dividendTaxFaqs: FaqItem[] = [
  {
    q: 'What is the Dividend Allowance for 2025/26?',
    a: 'The Dividend Allowance is £500 for 2025/26 — the first £500 of dividend income each tax year is tax-free, regardless of your other income or tax band.',
  },
  {
    q: 'What are the dividend tax rates for 2025/26?',
    a: 'Dividend income above the £500 allowance is taxed at 8.75% (basic rate), 33.75% (higher rate), or 39.35% (additional rate), depending on which tax band the dividend falls into once your other income has used its share of the bands.',
  },
  {
    q: 'Are dividend tax rates different in Scotland?',
    a: "No — unlike Income Tax on earned income, dividend tax rates and bands are set UK-wide and don't follow the Scottish Income Tax bands, even for Scottish taxpayers.",
  },
  {
    q: 'Do I need to pay dividend tax through Self Assessment?',
    a: 'If your dividend income is below £500 you usually don\'t need to report it. Above that, most people need to register for Self Assessment and declare dividend income on their tax return, even if tax is also deducted from other income through PAYE.',
  },
];

export const mortgageFaqs: FaqItem[] = [
  {
    q: 'How is a mortgage monthly repayment calculated?',
    a: 'On a repayment mortgage, the standard amortisation formula spreads the loan and interest evenly across the term, so each payment covers both interest and a growing share of the capital. On an interest-only mortgage, your monthly payment only covers the interest — the full loan amount is still owed at the end of the term.',
  },
  {
    q: 'What\'s the difference between repayment and interest-only mortgages?',
    a: "A repayment mortgage pays off both the interest and the capital over the term, so you own the property outright once it ends. An interest-only mortgage has lower monthly payments, but you still owe the full loan amount at the end and need a separate plan (savings, investments, or selling the property) to repay it.",
  },
  {
    q: 'Does this calculator include fees or early repayment charges?',
    a: "No — this shows the pure interest and capital repayment based on your loan amount, rate and term. It doesn't include arrangement fees, valuation fees, early repayment charges, or changes to the interest rate if you're on a fixed deal that ends partway through the term.",
  },
  {
    q: 'How much does a lower interest rate actually save?',
    a: 'Even a small rate difference compounds significantly over a 25-30 year term, since interest is calculated on the outstanding balance every month. Try the calculator with a rate 0.5-1% higher or lower than your current quote to see the real difference in total interest paid.',
  },
];

export const stampDutyFaqs: FaqItem[] = [
  {
    q: 'Does this cover Scotland or Wales?',
    a: "No — this calculator covers Stamp Duty Land Tax (SDLT), which applies in England and Northern Ireland only. Scotland has its own Land and Buildings Transaction Tax (LBTT), and Wales has Land Transaction Tax (LTT), both with different bands and rates.",
  },
  {
    q: 'What counts as a first-time buyer for stamp duty relief?',
    a: "You must never have owned a residential property anywhere in the world before, including inherited or gifted property, and you must be buying your only or main home. First-time buyer relief only applies if the purchase price is £500,000 or less — above that, standard rates apply to the full price.",
  },
  {
    q: 'Why is stamp duty on a second home so much higher?',
    a: "Additional properties, including second homes and buy-to-let purchases, carry a 5% surcharge on top of the standard SDLT rates, applied to the entire purchase price with no 0% band. This was increased from 3% to 5% at the Autumn Budget 2024.",
  },
  {
    q: 'Do I pay stamp duty on the whole price at one rate?',
    a: "No — like Income Tax, SDLT is banded. Only the portion of the price that falls within each band is taxed at that band's rate, not the whole amount at the highest rate that applies. The band breakdown on this page shows exactly how much tax falls into each band.",
  },
];

export const childBenefitFaqs: FaqItem[] = [
  {
    q: 'Is the High Income Child Benefit Charge based on household income?',
    a: "No — it's based on the higher earner's individual adjusted net income, not the combined income of both parents. A couple who each earn £59,000 (£118,000 combined) keep their Child Benefit in full, while a single-earner household on £70,000 faces a 50% charge.",
  },
  {
    q: 'Should I still claim Child Benefit if I know I\'ll pay it all back?',
    a: "Usually yes. Claiming (even if you opt out of receiving the payments) gives you National Insurance credits toward your State Pension, and it registers your child for a National Insurance number automatically. You can opt out of receiving the payments to avoid filing a return just for the charge, but you should still submit the claim form.",
  },
  {
    q: 'How do I actually pay the High Income Child Benefit Charge?',
    a: "Through Self Assessment. If your income is over £60,000 and you or your partner receive Child Benefit, the higher earner needs to register for Self Assessment and declare the charge on their tax return, even if they aren't self-employed.",
  },
  {
    q: 'Can pension contributions reduce the charge?',
    a: "Yes — the charge is based on adjusted net income, which is reduced by pension contributions made through salary sacrifice or relief-at-source schemes. Increasing pension contributions can lower your adjusted net income below £80,000 or even £60,000, reducing or eliminating the charge.",
  },
];

export const statutorySickPayFaqs: FaqItem[] = [
  {
    q: 'What are the 3 waiting days for SSP?',
    a: "The first 3 qualifying days of any period of sickness are unpaid, known as waiting days. SSP only becomes payable from the 4th qualifying day onwards. If you're off sick again within 8 weeks of a previous SSP period, the two periods can link and you may not have to serve waiting days again.",
  },
  {
    q: 'Do I have to earn a certain amount to qualify for SSP?',
    a: "Yes — you need to earn at least £125 a week on average (the Lower Earnings Limit for 2025/26) to qualify. If you earn less than this, you don't qualify for SSP, though you may be able to claim Employment and Support Allowance (ESA) instead.",
  },
  {
    q: 'Is Statutory Sick Pay the same as my normal salary?',
    a: "No — SSP is a flat weekly rate (£118.75 for 2025/26) regardless of your normal salary, unless your employer offers a more generous contractual sick pay scheme on top. Many employers pay full pay for a set period before dropping to SSP-only, so check your contract or staff handbook.",
  },
  {
    q: 'How long can I receive SSP for?',
    a: "SSP is payable for a maximum of 28 weeks for any one period of sickness, or for linked periods of sickness within an 8-week gap. After 28 weeks, you may be able to claim Employment and Support Allowance instead.",
  },
];

export const maternityPayFaqs: FaqItem[] = [
  {
    q: 'Why is the first 6 weeks paid differently to the rest?',
    a: "The first 6 weeks of Statutory Maternity Pay are paid at 90% of your average weekly earnings with no upper cap — so higher earners get a genuinely higher amount for those 6 weeks. From week 7 onwards, you're paid whichever is lower: the flat rate (£187.18 for 2025/26) or 90% of your average weekly earnings, which for most earners means the flat rate applies.",
  },
  {
    q: 'How is average weekly earnings worked out for SMP?',
    a: "It's based on what you earned, on average, over the 8 weeks up to and including your 'qualifying week' — the 15th week before your baby is due. This usually means your normal pay, but it can be affected by a recent pay rise, overtime, or bonuses paid during that reference period.",
  },
  {
    q: 'Do I need to have worked for my employer for a minimum amount of time?',
    a: "Yes — you need to have been continuously employed by the same employer for at least 26 weeks by the end of the qualifying week, and earn at least £125 a week on average. If you don't meet these conditions, you may still be able to claim Maternity Allowance from the government instead of SMP from your employer.",
  },
  {
    q: 'Does SMP cover the full 52 weeks of maternity leave?',
    a: "No — statutory maternity leave can last up to 52 weeks, but SMP is only paid for the first 39 of those. The final 13 weeks are unpaid unless your employer offers a more generous contractual maternity pay scheme.",
  },
];

export const teacherPayFaqs: FaqItem[] = [
  {
    q: 'What is the difference between the Main Pay Range and Upper Pay Range?',
    a: "The Main Pay Range (M1-M6) is where most teachers start and progress through in their early years. The Upper Pay Range (U1-U3) is reached by 'crossing the threshold' — from September 2025, the application process for this was removed, so progression is decided directly by your school's pay policy rather than a separate application.",
  },
  {
    q: 'Why is teacher pay different in London?',
    a: "Teachers in Inner London, Outer London, and the London Fringe area are paid on separate, higher pay scales to reflect the higher cost of living. Inner London rates are the highest, followed by Outer London, then the Fringe area, with 'rest of England' rates applying everywhere else (Wales uses the same rates as rest of England, despite having its own separate teacher pay body).",
  },
  {
    q: 'How is the Teachers\' Pension Scheme contribution worked out?',
    a: "TPS contributions are tiered based on your actual annual salary for that role, not the full-time equivalent. Rates run from 7.4% for the lowest tier up to 12% for salaries over £100,591 (2025/26 rates). Contributions are deducted before Income Tax under a net pay arrangement, so you get full tax relief automatically.",
  },
  {
    q: 'Does this apply to Scotland or Northern Ireland?',
    a: "No — this calculator uses the STPCD pay scales that apply in England and Wales. Scotland and Northern Ireland have their own separate teacher pay scales and negotiating bodies, with different salary points and structures.",
  },
  {
    q: 'How much does a teacher take home a month in 2025/26?',
    a: "In the rest of England, an ECT on M1 (£32,916) takes home around £2,110 a month after Income Tax, National Insurance and a 7.4% Teachers' Pension contribution. At the top of the Main Pay Range (M6, £45,352) it is about £2,745 a month, and on the Upper Pay Range (U3, £51,048) roughly £3,025. London pay areas and any TLR or SEN allowance would raise these figures.",
  },
  {
    q: 'Do teachers pay student loan repayments on top of pension?',
    a: "Yes. Student loan repayments are worked out separately from your Teachers' Pension contribution — they are 9% of income above your plan threshold (6% for a Postgraduate Loan), taken through payroll. Select your plan in the calculator to see the combined effect of tax, NI, pension and student loan on your take-home pay.",
  },
];

export const nurseFaqs: FaqItem[] = [
  {
    q: 'What is a newly qualified nurse salary in 2026/27?',
    a: "A newly qualified nurse starts on NHS Band 5, which runs from £32,073 to £39,043 for 2026/27 (after the 3.3% Agenda for Change award). At the Band 5 entry point in England with no London weighting, monthly take-home is roughly £2,040 after Income Tax, National Insurance and an 8.3% NHS pension contribution.",
  },
  {
    q: 'How does nurse pay progress from Band 5 to Band 7?',
    a: "Most nurses qualify onto Band 5, move to Band 6 as a senior or specialist nurse (£39,959–£48,117 for 2026/27), and can reach Band 7 as an advanced nurse practitioner, ward manager or clinical nurse specialist (£49,387–£56,515). Within each band you move up pay points automatically with service. Select your band and pay point in the calculator for the exact take-home.",
  },
  {
    q: 'Does this nurse take home pay calculator include the NHS pension?',
    a: "Yes. It applies the correct NHS Pension Scheme contribution tier for your pensionable pay (5.2% to 12.5%), deducted before Income Tax under a net pay arrangement. It also handles London HCAS weighting, unsocial-hours enhancements, part-time contracted hours and student loan repayments.",
  },
  {
    q: 'How much does a Band 6 nurse take home a month?',
    a: "A Band 6 nurse at the 2026/27 entry point (£39,959) takes home about £2,430 a month in England after tax, NI and a 9.8% pension contribution. Add HCAS in the calculator if you work in or around London, and enter your contracted hours if you are part-time.",
  },
  {
    q: 'Is nurse pay different in Scotland?',
    a: "Yes. NHS Scotland has its own Agenda for Change pay scales, and Scotland sets its own Income Tax bands. Switch the tax region to Scotland in the calculator for a Scottish take-home figure, though the band pay points shown are the England scales.",
  },
];

export const monthlyFaqs: FaqItem[] = [
  {
    q: 'How do I work out my monthly take-home pay from an annual salary?',
    a: "Divide the annual salary by 12 to get monthly gross, then subtract that month's share of Income Tax, National Insurance, pension and any student loan. PAYE spreads your tax-free Personal Allowance evenly across the year (£1,047.50 a month on the standard 1257L code), so on a steady salary your monthly take-home is the same every month. Enter your salary above for the exact figure.",
  },
  {
    q: 'Why is my monthly take-home pay different from one month to the next?',
    a: "Even on the same salary, months can differ because PAYE tax codes are cumulative — a pay rise, a bonus, a change of tax code, starting or repaying a student loan, or a week-53 pay run all shift a single month. Overtime and shift premiums also vary month to month. The calculator shows a normal month on your current settings.",
  },
  {
    q: 'Is this monthly salary calculator up to date for 2025/26?',
    a: "Yes. It uses the 2025/26 Income Tax bands, National Insurance thresholds and student loan repayment thresholds. Income Tax and NI thresholds are frozen through to April 2028, so 2025/26 figures also apply to 2026/27 and 2027/28 unless the government changes the rules at a Budget.",
  },
  {
    q: 'Does the monthly figure include pension and student loan?',
    a: "It can. Set your pension contribution and student loan plan in the calculator and the monthly take-home updates to include both. Pension contributions reduce the tax you pay; student loan repayments are 9% of income above your plan threshold (6% for a Postgraduate Loan), taken monthly through payroll.",
  },
];

export const taxFreeChildcareFaqs: FaqItem[] = [
  {
    q: 'Can I claim back childcare costs I\'ve already paid?',
    a: "Tax-Free Childcare works by topping up money you pay INTO an online childcare account, which you then use to pay your registered childcare provider — it's not a reimbursement for costs already paid outside the scheme. You can pay in as much or as little as you like, whenever you like, and the government adds its 20% top-up automatically.",
  },
  {
    q: 'What government help with childcare costs is available besides Tax-Free Childcare?',
    a: "Tax-Free Childcare sits alongside 30 hours of free childcare for eligible working parents of 3 and 4-year-olds (and increasingly younger children under recent expansions), and the childcare element of Universal Credit for those claiming it. You can't use Tax-Free Childcare at the same time as Universal Credit or childcare vouchers for the same child, so it's worth checking which one gives you more using the gov.uk childcare calculator.",
  },
  {
    q: 'Is there a minimum amount I need to pay in?',
    a: "No minimum — you can pay in any amount, whenever suits you, and top it up further before a big childcare bill. The only limit that matters is the cap on how much government top-up you can receive: £500 every 3 months per child (£1,000 if disabled).",
  },
  {
    q: 'Can grandparents or other family members pay into the account too?',
    a: "Yes — anyone can pay money into a Tax-Free Childcare account once it's set up, including grandparents, other relatives, or employers. The government top-up still applies to the total paid in each quarter, up to the same per-child cap.",
  },
];
